import React, { useEffect, useState } from 'react';

import cookieStorage from '@common/helpers/cookieStorage';

import useActiveBiddingOrder from '../hooks/useActiveBiddingOrder';
import FinishOrderBanner from './FinishOrderBanner';

// Cookie key holding the unique number of the order the customer dismissed.
const DISMISS_KEY = 'fin_nudge_dismissed_un';

// Gatekeeper around `FinishOrderBanner` — decides whether to mount it
// based on the current pathname. Rule:
//
//   Show on EVERY page the customer lands on after leaving the bidding
//   flow (first exit AND every later navigation in the same session),
//   EXCEPT:
//     · the bidding flow itself — /order, /bidding, /payment (you're in
//       it, no "go back" nudge)
//     · these account pages: /account (my orders), /account/profile,
//       /account/balance, /account/support, /account/loyalty-program,
//       /account/refer-a-friend
//   It DOES show on the rest of /account (order view) and on every
//   non-account page.
//
// Location tracking: we read `window.location.pathname` fresh at every
// render, and force a re-render on any of three events:
//   · `popstate`  — browser back / forward
//   · `pushstate` / `replacestate` (custom) — BiddingWizard SPA nav
//
// The custom events are emitted by a one-shot monkey-patch on
// `history.pushState` / `history.replaceState` (installed below). React-
// router-style SPA libraries all do this; native `history` API doesn't
// fire any event on these calls by default, which is the root cause of
// "banner sticks around on /bidding" when state flips via pushState.
function isBlockedRoute(pathname) {
    // Bidding flow itself — you're still IN it, no "go back" nudge.
    // Exact `/order` OR `/order/…` — but not `/orders` (legacy) or any
    // other path that happens to start with "order".
    const isOrderFlow = pathname === '/order' || pathname.startsWith('/order/');
    if (isOrderFlow || pathname.startsWith('/bidding') || pathname.startsWith('/payment')) {
        return true;
    }

    // Whole account area — `/account` and everything under it (profile,
    // balance, support, loyalty-program, my-orders, …). One rule instead
    // of a hand-maintained per-page list.
    if (pathname === '/account' || pathname.startsWith('/account/')) {
        return true;
    }

    return false;
}

// Install the patch exactly once per page load. Guarded by a module-
// level flag so N mounts of PendingOrderNudge don't stack N wrappers
// around the original method.
let historyPatched = false;
function patchHistoryForLocationEvents() {
    if (historyPatched || typeof window === 'undefined' || !window.history) return;
    historyPatched = true;

    const wrap = (method, eventName) => {
        const orig = window.history[method];
        if (typeof orig !== 'function') return;
        window.history[method] = function patched(...args) {
            const result = orig.apply(this, args);
            window.dispatchEvent(new Event(eventName));
            return result;
        };
    };

    wrap('pushState', 'pushstate');
    wrap('replaceState', 'replacestate');
}

export default function PendingOrderNudge({ authorized = false }) {
    // `tick` is a throw-away counter — we just need SOMETHING to
    // change so React re-renders and re-reads `window.location`.
    const [, setTick] = useState(0);

    useEffect(() => {
        patchHistoryForLocationEvents();
        const bump = () => setTick(t => t + 1);
        window.addEventListener('popstate', bump);
        window.addEventListener('pushstate', bump);
        window.addEventListener('replacestate', bump);
        return () => {
            window.removeEventListener('popstate', bump);
            window.removeEventListener('pushstate', bump);
            window.removeEventListener('replacestate', bump);
        };
    }, []);

    // Fresh read on every render — no stale state cache.
    const blocked = isBlockedRoute(window.location.pathname);

    // Fetch ONLY for authenticated customers. Two reasons:
    //   1. A guest has no bidding orders, so the banner can never show —
    //      the call is pure waste for them.
    //   2. Critical on the dev box: it sits behind HTTP basic-auth. The
    //      browser attaches the cached basic-auth creds to every same-origin
    //      request, including this XHR. For a guest, GET /api/bidding/orders
    //      returns app-level 401 — and the browser reads "401 on a request I
    //      sent Basic creds with" as "creds rejected", drops them, and
    //      re-pops the sign-in dialog on the next navigation. Never firing it
    //      for guests removes that whole re-prompt loop.
    // Also skip on blocked routes (bidding flow / account) — nothing to show.
    const { order, bidsCount } = useActiveBiddingOrder({ enabled: !blocked && authorized });

    if (blocked || !order) return null;

    // Already dismissed for this exact order → stay hidden (cookie survives
    // navigation + reload until a new pending order appears).
    const un = order.unique_number || order.uniqueNumber;
    if (un && cookieStorage.get(DISMISS_KEY) === String(un)) return null;

    const handleDismiss = () => {
        if (!un) return;
        cookieStorage.set(DISMISS_KEY, un);
        setTick(t => t + 1); // re-render so the banner unmounts
    };

    // Pass the pathname so the banner re-shows on EVERY navigation within
    // the session (until dismissed), not just the first exit.
    return (
        <FinishOrderBanner
            order={order}
            bidsCount={bidsCount}
            pathname={window.location.pathname}
            onDismiss={handleDismiss}
        />
    );
}
